import { useEffect, useState, useCallback } from "react";
import "./App.css";
import { IApiService, ReportResponse } from "./apiService";

type Period = "day" | "week" | "month";

interface Props {
    apiService: IApiService;
    user: { name: string; email: string; token: string };
    isAdmin: boolean;
    onBack: () => void;
}

export default function ReportsPage({ apiService, user, isAdmin, onBack }: Props) {
    const [period, setPeriod] = useState<Period>("week");
    const [email, setEmail] = useState(user.email);
    const [data, setData] = useState<ReportResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setEmail(user.email);
    }, [user]);

    const fetchReport = useCallback(
        async (e?: React.FormEvent) => {
            if (e) e.preventDefault();
            setLoading(true);
            setError(null);
            setData(null);
            try {
                const targetEmail = isAdmin && email !== user.email ? email : null;
                const result = await apiService.getReport(user.token, period, targetEmail);
                switch (result.type) {
                    case "ok":
                        setData(result.data);
                        break;

                    case "expired":
                        setError("Session expired, please log in again.");
                        break;

                    default:
                        setError("Failed to fetch report");
                        break;
                }
            } catch (err: any) {
                setError(err.message);
            }
            setLoading(false);
        },
        [apiService, user, isAdmin, email, period],
    );

    useEffect(() => {
        // void here avoids linter error on not using await on async function
        void fetchReport();
    }, [period, email, fetchReport]);

    return (
        <>
            <button className="menu-link" onClick={onBack} type="button">
                ← Back
            </button>
            <form onSubmit={fetchReport} style={{ width: "100%" }}>
                <div className="menu" style={{ margin: "1em 0" }}>
                    <label>
                        <span style={{ marginRight: 8 }}>Period:</span>
                        <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                            <option value="month">Monthly</option>
                        </select>
                    </label>
                    {isAdmin && (
                        <input
                            style={{
                                marginLeft: 16,
                                minWidth: 180,
                                fontSize: "1rem",
                                padding: "0.5em 0.8em",
                                borderRadius: 8,
                            }}
                            type="email"
                            placeholder="User email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    )}
                    <button className="menu-link" style={{ padding: "0.5em 1.2em" }} type="submit" disabled={loading}>
                        Run
                    </button>
                </div>
            </form>
            {loading && <div style={{ margin: "1.5em 0" }}>Loading...</div>}
            {error && <div style={{ color: "#d22", margin: "1.5em 0" }}>{error}</div>}
            {data && (
                <div style={{ width: "100%" }}>
                    <div className="support-text" style={{ textAlign: "left" }}>
                        <strong>User:</strong> {data.email}
                        <br />
                        <strong>Grouped by:</strong> {data.period}
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1em" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #e4e8f0" }}>
                                <th align="left">Period</th>
                                <th align="right">Hours</th>
                                <th align="left">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.report.map((row) => (
                                <tr key={row.period} style={{ borderBottom: "1px solid #f0f1f4" }}>
                                    <td>{new Date(row.period).toLocaleDateString()}</td>
                                    <td align="right">{(row.seconds / 3600).toFixed(2)}</td>
                                    <td>{row.notes.filter(Boolean).join(", ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.report.length === 0 && <div style={{ margin: "1.5em 0" }}>No entries for this period.</div>}
                </div>
            )}
        </>
    );
}
